import { ErrorResponse, type User, type UserManager } from 'oidc-client-ts';
import { OIDC_TOKEN_LEEWAY_SECONDS } from '@/auth/constants';
import { handleAuthError } from '@/auth/session';

let userManager: UserManager | undefined;
let refreshRequest: Promise<string | undefined> | undefined;

export const setUserManager = (manager: UserManager) => {
  userManager = manager;
};

const hasAccessTokenExpired = ({ expires_in }: User): boolean =>
  expires_in !== undefined && expires_in <= OIDC_TOKEN_LEEWAY_SECONDS;

const refreshAccessToken = async (
  manager: UserManager,
): Promise<string | undefined> => {
  try {
    const user = await manager.signinSilent();

    return user?.access_token;
  } catch (error) {
    const isRejectedByServer = error instanceof ErrorResponse;

    if (isRejectedByServer) {
      handleAuthError();
    }

    throw error;
  } finally {
    refreshRequest = undefined;
  }
};

export const getAccessToken = async (): Promise<string | undefined> => {
  if (!userManager) {
    return undefined;
  }

  const user = await userManager.getUser();

  if (!user) {
    return undefined;
  }

  if (!hasAccessTokenExpired(user) || !user.refresh_token) {
    return user.access_token;
  }

  if (!refreshRequest) {
    refreshRequest = refreshAccessToken(userManager);
  }

  return refreshRequest;
};
